/** Shared style helpers. Every value a component computes resolves to an `--al-*` token. */

import type { CSSProperties } from "react";

/** A step on the 4px scale in `design-system.css`. `gap={5}` is `--al-space-5`, 16px. */
export type SpaceStep = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

/** The token for a step, so nothing in a component names a pixel. */
export function spaceVar(step: SpaceStep | undefined): string | undefined {
  return step === undefined ? undefined : `var(--al-space-${step})`;
}

/**
 * Merge computed style over a caller's `style`, dropping every undefined entry.
 * `exactOptionalPropertyTypes` refuses an explicit undefined on a `CSSProperties` key, so a
 * component builds its style here rather than in an object literal.
 */
export function mergeStyle(
  base: CSSProperties | undefined,
  computed: Record<string, string | number | undefined>,
): CSSProperties {
  const out: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(computed)) {
    if (value !== undefined) out[key] = value;
  }
  return { ...(out as CSSProperties), ...base };
}

/** A size in CSS, from either a number of pixels or a full length. */
export function lengthOf(value: string | number | undefined): string | undefined {
  if (value === undefined) return undefined;
  return typeof value === "number" ? `${value}px` : value;
}
